/**
 * API client.
 *
 * Two things worth noting:
 *
 *  - Uploads never go through this client's `request`. They go to a presigned
 *    S3 URL directly (FR-3), because pushing a 3 MB receipt through API Gateway
 *    would hit the payload limit and cost per request for no benefit.
 *
 *  - The server's error shapes are preserved rather than flattened to a string.
 *    A hard block, a missing requirement, and a validation error need different
 *    UI treatment, and collapsing them into `Error(message)` throws that away.
 */
import { getAccessToken, login } from './auth.js';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly detail?: unknown,
    readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Tier 1 (FR-59 – FR-62): not overridable. The UI must not offer a retry. */
  get isHardBlock(): boolean {
    return this.code === 'hard_block';
  }

  /** Satisfiable by attaching a document or filling a field. */
  get isMissingRequirement(): boolean {
    return this.code === 'missing_requirement';
  }

  get remedy(): string | null {
    const d = this.detail as { remedy?: string } | undefined;
    return d?.remedy ?? null;
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  opts: { idempotencyKey?: string } = {},
): Promise<T> {
  const token = await getAccessToken();
  if (!token) {
    await login(window.location.pathname);
    throw new ApiError(401, 'unauthenticated', 'redirecting to sign in');
  }

  const headers: Record<string, string> = { authorization: `Bearer ${token}` };
  if (body !== undefined) headers['content-type'] = 'application/json';
  if (opts.idempotencyKey) headers['idempotency-key'] = opts.idempotencyKey;

  const res = await fetch(path, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  const text = await res.text();
  const isJson = (res.headers.get('content-type') ?? '').includes('json');
  const payload: unknown = isJson && text ? JSON.parse(text) : text;

  if (!res.ok) {
    const p = payload as { error?: string; message?: string; detail?: unknown; request_id?: string };
    throw new ApiError(
      res.status,
      p?.error ?? 'error',
      p?.message ?? `${method} ${path} failed with ${res.status}`,
      p?.detail,
      p?.request_id,
    );
  }
  return payload as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown, key?: string) =>
    request<T>('POST', path, body ?? {}, { idempotencyKey: key }),
  patch: <T>(path: string, body: unknown) => request<T>('PATCH', path, body),
  put: <T>(path: string, body: unknown) => request<T>('PUT', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
};

/**
 * Base64 SHA-256 of a blob, for `x-amz-checksum-sha256`.
 *
 * The archive bucket has Object Lock default retention, and S3 refuses any
 * PutObject against it that carries no integrity header:
 *
 *   Content-MD5 OR x-amz-checksum- HTTP header is required for Put Object
 *   requests with Object Lock parameters
 *
 * MD5 is the other accepted option and WebCrypto deliberately does not
 * implement it, so SHA-256 is the one that works without shipping a hash
 * library. The value has to be signed into the presigned URL as well as sent
 * on the wire, which is why it is computed before asking for the URL rather
 * than after.
 *
 * crypto.subtle needs a secure context. The app is HTTPS behind CloudFront,
 * and localhost counts as secure, so both real and dev origins are fine.
 */
async function sha256Base64(blob: Blob): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await blob.arrayBuffer());
  let binary = '';
  for (const byte of new Uint8Array(digest)) binary += String.fromCharCode(byte);
  return btoa(binary);
}

/**
 * FR-3 / FR-4 — direct-to-S3 upload.
 *
 * The downscale happens before the presign so the request never carries a
 * 12-megapixel phone photo. FR-4 sets the long edge at ~2000px, which keeps a
 * receipt legible while cutting a typical upload from ~4 MB to ~350 KB — the
 * difference between a capture that completes on a pharmacy's cell signal and
 * one that does not.
 */
export async function uploadReceipt(
  file: File,
  opts: { expenseId?: string } = {},
): Promise<{ expenseId: string; key: string }> {
  const prepared = await downscale(file);
  // Computed before presigning: the digest is part of what gets signed.
  const checksum = await sha256Base64(prepared);

  const presigned = await api.post<{ url: string; key: string; expense_id: string }>(
    '/api/uploads/receipt',
    {
      filename: prepared.name,
      content_type: prepared.type,
      expense_id: opts.expenseId,
      checksum_sha256: checksum,
    },
  );

  const res = await fetch(presigned.url, {
    method: 'PUT',
    headers: {
      'content-type': prepared.type,
      // Must match the ServerSideEncryption in the signed request, or S3
      // rejects the PUT with a signature mismatch that reads as a 403.
      'x-amz-server-side-encryption': 'AES256',
      // Required by Object Lock, and signed above — both must be present and
      // identical or S3 rejects the PUT.
      'x-amz-checksum-sha256': checksum,
    },
    body: prepared,
  });
  if (!res.ok) throw new Error(`upload failed: ${res.status} ${await res.text()}`);

  return { expenseId: presigned.expense_id, key: presigned.key };
}

/**
 * The establishment-date confirmation (PRD §5.4).
 *
 * Deliberately not downscaled: this is a custodian document, and it is the one
 * artifact whose legibility an examiner may turn on. Receipts get compressed
 * because a blurry receipt is still a receipt; a compressed PDF of the letter
 * proving your account's establishment date is a worse version of the only
 * thing that proves it.
 *
 * Two steps, because the bytes never transit the API (FR-3, TR-40): presign,
 * PUT direct to S3, then record the key on the config so the packet builder
 * can find it.
 */
export async function uploadEstablishmentEvidence(file: File): Promise<{ key: string }> {
  const contentType = file.type || 'application/octet-stream';
  const checksum = await sha256Base64(file);

  const presigned = await api.post<{ url: string; key: string }>(
    '/api/config/establishment-evidence',
    { filename: file.name, content_type: contentType, checksum_sha256: checksum },
  );

  const res = await fetch(presigned.url, {
    method: 'PUT',
    headers: {
      'content-type': contentType,
      // The archive bucket policy denies any PUT without this header, and
      // default bucket encryption does not satisfy it — see docs/security.md.
      'x-amz-server-side-encryption': 'AES256',
      // Required by Object Lock on the archive bucket — see sha256Base64.
      'x-amz-checksum-sha256': checksum,
    },
    body: file,
  });
  if (!res.ok) throw new Error(`upload failed: ${res.status} ${await res.text()}`);

  // Only now is the key real. Recording it before the PUT succeeds would leave
  // the config pointing at an object that does not exist.
  await api.put('/api/config', { establishment_date_evidence_s3_key: presigned.key });

  return { key: presigned.key };
}

export async function uploadImport(
  file: File,
  channel: string,
): Promise<{ runId: string; key: string }> {
  const checksum = await sha256Base64(file);

  const presigned = await api.post<{ url: string; key: string; run_id: string }>(
    '/api/uploads/import',
    {
      filename: file.name,
      content_type: file.type || 'application/zip',
      channel,
      checksum_sha256: checksum,
    },
  );

  const res = await fetch(presigned.url, {
    method: 'PUT',
    headers: {
      'content-type': file.type || 'application/zip',
      'x-amz-server-side-encryption': 'AES256',
      'x-amz-checksum-sha256': checksum,
    },
    body: file,
  });
  if (!res.ok) throw new Error(`upload failed: ${res.status} ${await res.text()}`);

  return { runId: presigned.run_id, key: presigned.key };
}

/**
 * FR-4 — client-side downscale to ~2000px on the long edge, keeping the
 * original if it is already reasonable.
 *
 * HEIC is passed through untouched: Safari will not decode it into a canvas,
 * and a failed re-encode would be worse than a larger upload.
 */
const MAX_EDGE = 2000;
const PASSTHROUGH = /^image\/(heic|heif)$/i;

async function downscale(file: File): Promise<File> {
  if (!file.type.startsWith('image/') || PASSTHROUGH.test(file.type)) return file;

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return file; // unreadable by the browser — send it as-is and let the server try
  }

  if (bitmap.width <= MAX_EDGE && bitmap.height <= MAX_EDGE) {
    bitmap.close();
    return file;
  }

  const scale = MAX_EDGE / Math.max(bitmap.width, bitmap.height);
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    bitmap.close();
    return file;
  }
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();

  const blob = await new Promise<Blob | null>((resolve) =>
    // 0.85 is high enough that faded thermal print survives, which is the whole
    // reason this archive exists.
    canvas.toBlob(resolve, 'image/jpeg', 0.85),
  );
  if (!blob) return file;

  const name = file.name.replace(/\.[^.]+$/, '') + '.jpg';
  return new File([blob], name, { type: 'image/jpeg', lastModified: file.lastModified });
}
